/**
 * Block 12 PR-B Phase B3 Part C Step 16 — SectionParams (А).
 *
 * Renders form fields из `methodology.fields[]`. Per ADR-0020 §Q1 hybrid:
 * structure через JSON, components через `pickFieldComponent` mapping в
 * `<NumberInput>` / `<RadioCard>` / `<PickerSheet>` / `<TextInput>` (B1 frozen).
 *
 * Local state: `activePicker: string | null` — which `'picker'` field's
 * BottomSheet open сейчас. Closes на onSelect или onClose.
 *
 * Pure presenter — все handlers controlled через props (onParamChange).
 *
 * Reference: ADR-0020 §Q1 + §Q7 (inline validation errors per field).
 */

import { Fragment, useState } from 'react';
import type { CalculatorAnketaType, CalculatorMethodology } from '@pomoshnik/shared';
import { NumberInput } from '@/components/forms/NumberInput';
import { RadioCard, type RadioCardOption } from '@/components/forms/RadioCard';
import { TextInput } from '@/components/forms/TextInput';
import { PickerSheet, type PickerOption } from '@/components/PickerSheet/PickerSheet';
import { SectionCard } from '../components/SectionCard';
import { SectionHeader } from '../components/SectionHeader';
import { SECTION_PARAMS_HEADER } from '../anketaTypeLabels';
import {
  BOOLEAN_OPTIONS,
  booleanFromLabel,
  booleanToLabel,
  pickFieldComponent,
  schemaValueToLabel,
  selectToSchemaValue,
  type AnketaField,
} from '../helpers/anketaUiMapping';
import type { AnketaParams } from '../types';

/**
 * Hotfix-2B Phase B5 Step 11 — TV-зона subgrouping (Bug 10).
 *
 * Per прототип 2.12.7:346-385 поля Section А разделены на две подсекции:
 *   - ТУМБА — length / height / depth (обязательные)
 *   - ШКАФ — hasShelf checkbox + shelfLength/Height/Depth (условные)
 *
 * Маппинг хранится здесь (а не в methodology JSON), потому что `CalculatorFieldSchema`
 * не имеет `subgroup` поля — добавление было бы contract change. Phase B5 явный
 * scope: no-contract-change, поэтому subgroup'инг полностью render-side.
 *
 * Прочие anketa types — пустая запись → flat list без headings (regression-safe).
 */
const FIELD_SUBGROUP_MAP: Partial<Record<CalculatorAnketaType, Record<string, string>>> = {
  'tv-zone': {
    itemName: 'ТУМБА',
    length: 'ТУМБА',
    height: 'ТУМБА',
    depth: 'ТУМБА',
    hasShelf: 'ШКАФ',
    shelfLength: 'ШКАФ',
    shelfHeight: 'ШКАФ',
    shelfDepth: 'ШКАФ',
    // hotfix-2C Phase C2 Step 5 — handleType moved from Section Б inline picker
    // to Params; per Schemas v2 (line 289-290) belongs to «ОБЩИЕ» subgroup.
    handleType: 'ОБЩИЕ',
  },
};

/**
 * Hotfix-2B Phase B5 Step 11 — shelf*-поля TV-зоны скрываются когда `hasShelf !== true`.
 * Same pattern что `length2` + `isAngular` (Bug #7) — не показываем пользователю
 * поля, значения которых формулами игнорируются. Per прототип 2.12.7:368.
 */
const TV_ZONE_SHELF_FIELDS = new Set(['shelfLength', 'shelfHeight', 'shelfDepth']);

export interface SectionParamsProps {
  fields: CalculatorMethodology['fields'];
  params: AnketaParams;
  onParamChange: (field: string, value: unknown) => void;
  errorForPath: (path: string) => string | undefined;
}

export function SectionParams({ fields, params, onParamChange, errorForPath }: SectionParamsProps) {
  const [activePicker, setActivePicker] = useState<string | null>(null);
  const heading = SECTION_PARAMS_HEADER[params.type];
  const subgroupMap = FIELD_SUBGROUP_MAP[params.type] ?? {};
  const paramsRecord = params as Record<string, unknown>;

  // Phase B5 Step 11 — track previous subgroup для injection heading'а перед
  // первым полем каждой группы. Reset на field без subgroup (subgroup=undefined).
  let prevSubgroup: string | null = null;

  return (
    <SectionCard testId="section-params">
      <SectionHeader>{heading}</SectionHeader>
      <div className="flex flex-col gap-4">
        {fields.map((field) => {
          const value = paramsRecord[field.name];
          const error = errorForPath(field.name);
          const ui = pickFieldComponent(field);

          // Bug #7 — `length2` имеет смысл только для угловой конфигурации
          // (Kitchen/Cabinet/Balcony). Скрываем поле когда isAngular !== true,
          // чтобы пользователь не вводил данные, которые игнорируются формулами.
          // Generic record access потому что `isAngular` отсутствует в variants без
          // углового режима (например, bath-tabletop).
          if (field.name === 'length2' && paramsRecord.isAngular !== true) {
            return null;
          }

          // Phase B5 Step 11 — TV-зона: shelf* fields visible только когда
          // hasShelf=true. Heading «ШКАФ» рендерится независимо от значения
          // (она группирует сам checkbox hasShelf).
          if (
            params.type === 'tv-zone' &&
            TV_ZONE_SHELF_FIELDS.has(field.name) &&
            paramsRecord.hasShelf !== true
          ) {
            return null;
          }

          // PR-A1 (ADR-0033) — countertopSheetLen видно ТОЛЬКО для ЛДСП. HPL
          // фикс 4000 (выбора нет), камень — пог. метры (лист неприменим).
          // Скрываем поле для всех kinds кроме «Столешницы ЛДСП», чтобы не
          // показывать выбор, который формула игнорирует (тот же паттерн что
          // length2 / shelf* visibility guards выше).
          if (
            field.name === 'countertopSheetLen' &&
            paramsRecord.countertopKind !== 'Столешницы ЛДСП'
          ) {
            return null;
          }

          // Phase B5 Step 11 — subgroup heading injection. Эмитим heading
          // только при ПЕРВОМ field конкретной subgroup (или при смене группы).
          const subgroup = subgroupMap[field.name] ?? null;
          const emitHeading = subgroup !== null && subgroup !== prevSubgroup;
          prevSubgroup = subgroup;

          const fieldElement = renderField({
            field,
            ui,
            value,
            error,
            params,
            activePicker,
            setActivePicker,
            onParamChange,
          });

          if (!emitHeading) return fieldElement;
          return (
            <Fragment key={`group-${field.name}`}>
              <h3
                className="text-ink font-bold uppercase tracking-wider mt-2"
                style={{ fontSize: '13px', letterSpacing: '0.08em' }}
                data-testid={`section-params-subgroup-${subgroup}`}
              >
                {subgroup}
              </h3>
              {fieldElement}
            </Fragment>
          );
        })}
        <TvZoneVitrineHint params={params} />
      </div>
    </SectionCard>
  );
}

/**
 * Phase B5 Step 11 — Field rendering extracted в helper для clean subgroup
 * heading wrap pattern. Тот же switch on `ui` что был inline; теперь caller
 * (SectionParams) делает heading-injection до вызова.
 */
interface RenderFieldArgs {
  field: AnketaField;
  ui: ReturnType<typeof pickFieldComponent>;
  value: unknown;
  error: string | undefined;
  params: AnketaParams;
  activePicker: string | null;
  setActivePicker: (name: string | null) => void;
  onParamChange: (field: string, value: unknown) => void;
}

function renderField({
  field,
  ui,
  value,
  error,
  params,
  activePicker,
  setActivePicker,
  onParamChange,
}: RenderFieldArgs) {
  if (ui === 'number') {
    return (
      <NumberInput
        key={field.name}
        name={field.name}
        label={field.label}
        value={typeof value === 'number' ? value : null}
        onChange={(v) => onParamChange(field.name, v)}
        min={field.min}
        max={field.max}
        required={field.required}
        hint={field.helpText}
        error={error}
      />
    );
  }

  if (ui === 'radio') {
    return (
      <FieldRadio
        key={field.name}
        anketaType={params.type}
        field={field}
        value={value}
        onChange={(v) => onParamChange(field.name, v)}
        error={error}
      />
    );
  }

  if (ui === 'picker') {
    return (
      <FieldPicker
        key={field.name}
        field={field}
        value={typeof value === 'string' ? value : null}
        isOpen={activePicker === field.name}
        onOpen={() => setActivePicker(field.name)}
        onClose={() => setActivePicker(null)}
        onSelect={(v) => onParamChange(field.name, v)}
        error={error}
      />
    );
  }

  // ui === 'text'
  return (
    <TextInput
      key={field.name}
      name={field.name}
      label={field.label}
      value={typeof value === 'string' ? value : ''}
      onChange={(v) => onParamChange(field.name, v)}
      required={field.required}
      hint={field.helpText}
      error={error}
      maxLength={100}
    />
  );
}

/**
 * Hint для TV-зона анкеты когда vitrineProfile=true (per прототип 2.12.7:379):
 * «Витрина включена — фасад шкафа считается только по двум глубинам»
 *
 * Условие render: anketaType='tv-zone' AND vitrineProfile=true AND hasShelf=true
 * (per прототип — hint только внутри hasShelf block).
 *
 * Style: fontSize 13, color GR (text-muted) per прототип. NB: TZ Step 11 spec'нул
 * ⚠ prefix + warning color #d97706 — отклонено в пользу prototype-fidelity
 * (Phase A1 review precedent: prototype-first выигрывает над TZ literal).
 */
function TvZoneVitrineHint({ params }: { params: AnketaParams }) {
  if (params.type !== 'tv-zone') return null;
  const record = params as Record<string, unknown>;
  if (record.vitrineProfile !== true) return null;
  if (record.hasShelf !== true) return null;
  return (
    <p
      role="note"
      className="text-muted m-0"
      style={{ fontSize: '13px' }}
      data-testid="tv-zone-vitrine-hint"
    >
      Витрина включена — фасад шкафа считается только по двум глубинам
    </p>
  );
}

// ── Internal sub-components ─────────────────────────────────────────────────

interface FieldRadioProps {
  anketaType: AnketaParams['type'];
  field: AnketaField;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
}

function FieldRadio({ anketaType, field, value, onChange, error }: FieldRadioProps) {
  // boolean → 2 fixed options Да/Нет, string conversion at boundary.
  if (field.kind === 'boolean') {
    const opts: RadioCardOption[] = BOOLEAN_OPTIONS.map((label) => ({ value: label, label }));
    return (
      <RadioCard
        name={field.name}
        label={field.label}
        options={opts}
        value={booleanToLabel(typeof value === 'boolean' ? value : undefined) ?? null}
        onChange={(label) => {
          const b = booleanFromLabel(label);
          if (b !== undefined) onChange(b);
        }}
        required={field.required}
        hint={field.helpText}
        error={error}
      />
    );
  }

  // select с ≤3 options — конвертация schema value ↔ UI label через
  // selectToSchemaValue/schemaValueToLabel (Bug #4). Поля без маппинга
  // (free string select) проходят через graceful passthrough.
  const opts: RadioCardOption[] = (field.options ?? []).map((opt) => ({ value: opt, label: opt }));
  const currentLabel = schemaValueToLabel(anketaType, field.name, value);
  return (
    <RadioCard
      name={field.name}
      label={field.label}
      options={opts}
      value={currentLabel === '' ? null : currentLabel}
      onChange={(label) => onChange(selectToSchemaValue(anketaType, field.name, label))}
      required={field.required}
      hint={field.helpText}
      error={error}
    />
  );
}

interface FieldPickerProps {
  field: AnketaField;
  value: string | null;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (v: string) => void;
  error?: string;
}

function FieldPicker({ field, value, isOpen, onOpen, onClose, onSelect, error }: FieldPickerProps) {
  const opts: PickerOption[] = (field.options ?? []).map((opt) => ({ value: opt, label: opt }));
  const hasError = Boolean(error);
  return (
    <div className="flex flex-col">
      <span className="text-ink font-semibold text-base mb-2">
        {field.label}
        {field.required ? (
          <span aria-hidden="true" className="text-error ml-1">
            *
          </span>
        ) : null}
      </span>
      <button
        type="button"
        onClick={onOpen}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-invalid={hasError || undefined}
        className={
          'min-h-tap rounded-xl border-[1.5px] px-4 py-3 text-left text-base text-ink bg-card ' +
          (hasError ? 'border-error ' : 'border-accent ') +
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none'
        }
      >
        {value ?? 'Выберите...'}
      </button>
      {error ? (
        <p role="alert" className="text-error text-sm mt-1">
          {error}
        </p>
      ) : field.helpText ? (
        <p className="text-muted text-sm mt-1">{field.helpText}</p>
      ) : null}
      <PickerSheet
        isOpen={isOpen}
        title={field.label}
        options={opts}
        selectedValue={value}
        onSelect={onSelect}
        onClose={onClose}
      />
    </div>
  );
}
